import React from 'react';
import * as Mui from '@mui/material';
import { withStyles } from "tss-react/mui";
import type { WithStyles } from "type/tss";
import { connect } from 'react-redux';
import { bindActionCreators, Dispatch } from 'redux';
import { RootState } from 'redux/store';
import { styles } from './styles';
import DragIndicatorIcon from '@mui/icons-material/DragIndicator';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import * as Icons from "react-feather";
import { Avatar } from "component/shared/avatar";
import { Agent } from 'model/agent';

interface Props extends OwnProps,
	ReturnType<typeof mapStateToProps>,
	ReturnType<typeof mapDispatchToProps>,
	WithStyles<typeof styles> {
	theme: Mui.Theme;
}

interface OwnProps {
	agent: Agent;
	position: number;
	inputValue: string;
	onPositionChange: (value: string) => void;
	onDelete: () => void;
	error?: string;
	isLast?: boolean;
}

interface State {
}

const mapStateToProps = (state: RootState, ownProps: OwnProps) => {
	return {
	};
};

const mapDispatchToProps = (dispatch: Dispatch) => {
	return bindActionCreators({
	}, dispatch);
};

class Component extends React.Component<Props, State> {
	constructor(props: Props) {
		super(props);
		this.state = {
		};
	}

	render() {
		const { classes = {}, agent, onDelete, onPositionChange, inputValue, error, theme, isLast } = this.props;
		const numValue = parseInt(inputValue, 10);
		return (
			<Mui.Tooltip title={agent.enableAgentLink ? "" : "This agent will not appear in the agent list because 'Enable Link on Agent Roster' is turned off"}>
				<Mui.Box
					className={agent.enableAgentLink ? classes.cardContent : classes.greyCardContent}
					style={{ borderBottom: isLast ? 'none' : '1px solid rgba(0,0,0,0.12)' }}
				>
					<Mui.Grid container direction="row" spacing={1} alignItems="center">
						<Mui.Grid item>
							<DragIndicatorIcon />
						</Mui.Grid>
						<Mui.Grid item style={{ flex: 1 }}>
							<Mui.Box display="flex" alignItems="center" flexDirection="row">
								<Avatar
									phrase={agent.label}
									src={agent.photoUrl}
									color={theme.palette.primary.main}
								/>
								<Mui.Typography className={classes.agentLabel}>
									{agent.label}
								</Mui.Typography>
							</Mui.Box>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.Box display="flex" flexDirection="column" alignItems="flex-end">
								<Mui.Box
									style={{
										border: `1px solid ${error ? '#f44336' : 'rgba(0,0,0,0.23)'}`,
										borderRadius: 4,
										display: 'flex',
										overflow: 'hidden',
										width: 120,
										height: 48,
									}}
								>
									<Mui.Box
										style={{
											flex: 1,
											display: 'flex',
											alignItems: 'center',
											paddingLeft: 16,
										}}
									>
										<Mui.Typography style={{ fontSize: 16, color: 'rgba(0,0,0,0.54)' }}>
											{inputValue}
										</Mui.Typography>
									</Mui.Box>
									<Mui.Box style={{ width: 1, backgroundColor: 'rgba(0,0,0,0.12)', alignSelf: 'stretch' }} />
									<Mui.Box
										style={{
											display: 'flex',
											flexDirection: 'column',
											width: 44,
											justifyContent: 'center',
											alignItems: 'center',
										}}
									>
										<Mui.IconButton
											size="small"
											color="primary"
											style={{ padding: 2 }}
											onClick={() => onPositionChange(String(numValue + 1))}
										>
											<Icons.ChevronUp size={16} />
										</Mui.IconButton>
										<Mui.IconButton
											size="small"
											color="primary"
											style={{ padding: 2 }}
											onClick={() => onPositionChange(String(Math.max(1, numValue - 1)))}
										>
											<Icons.ChevronDown size={16} />
										</Mui.IconButton>
									</Mui.Box>
								</Mui.Box>
								{error && (
									<Mui.Box display="flex" alignItems="center" className={classes.errorText}>
										<Icons.XCircle size={14} color="#f44336" style={{ flexShrink: 0, marginRight: 4 }} />
										<Mui.Typography variant="caption">
											{error}
										</Mui.Typography>
									</Mui.Box>
								)}
							</Mui.Box>
						</Mui.Grid>
						<Mui.Grid item>
							<Mui.IconButton size="small" onClick={onDelete} aria-label="Remove agent">
								<DeleteOutlinedIcon fontSize="small" style={{ color: theme.palette.primary.main }} />
							</Mui.IconButton>
						</Mui.Grid>
					</Mui.Grid>
				</Mui.Box>
			</Mui.Tooltip>
		);
	}
}

const ConnectedComponent = connect(mapStateToProps, mapDispatchToProps)(Component);

// tss-react's withStyles does not inject a `theme` prop the way the old JSS bridge's
// `{ withTheme: true }` did; this class component reads `theme.palette` in render, so inject it via useTheme.
const ThemedComponent = (props: OwnProps & WithStyles<typeof styles>) => {
	const theme = Mui.useTheme();
	return <ConnectedComponent {...props} theme={theme} />;
};

export const Card = withStyles(ThemedComponent, styles);
